import axios, { AxiosError } from 'axios';
import { API_URL } from './api';
import { clearSession as clearOpenReportingSession } from './creators/OpenReporting.creators';
import { clearSession } from './creators/User.creators';
import * as Types from './types';
import store from './store';

const D1P_SESSION_REFRESH_URL = '/portal/open/sessions/refresh';

let isRefreshing = false;
let refreshSubscribers: Array<(refreshed: boolean) => void> = [];

const subscribeToRefresh = (callback: (refreshed: boolean) => void) => {
  refreshSubscribers.push(callback);
};

const notifySubscribers = (refreshed: boolean) => {
  refreshSubscribers.forEach((cb) => cb(refreshed));
  refreshSubscribers = [];
};

const attemptD1PSessionRefresh = async (): Promise<boolean> => {
  try {
    const response = await axios.post(`${API_URL}${D1P_SESSION_REFRESH_URL}`);
    const token = response?.headers?.['x-session'];
    if (token) {
      store.dispatch({
        type: Types.UserTypes.SESSION,
        payload: { session: token },
      });
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const instance = axios.create({
  baseURL: API_URL,
});

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    if (error?.response?.status === 401) {
      const requestUrl = error.response.request?.responseURL ?? '';

      if (requestUrl.includes(D1P_SESSION_REFRESH_URL)) {
        store.dispatch(clearSession());
        return Promise.reject(error);
      }

      if (requestUrl.includes?.('/public')) {
        store.dispatch(clearOpenReportingSession());
      } else if (requestUrl.includes?.('/portal')) {
        if (!error.config) {
          store.dispatch(clearSession());
          return Promise.reject(error);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          const refreshed = await attemptD1PSessionRefresh();
          isRefreshing = false;
          notifySubscribers(refreshed);

          if (refreshed) {
            return instance.request(error.config);
          }

          store.dispatch(clearSession());
        } else {
          return new Promise((resolve, reject) => {
            subscribeToRefresh((refreshed) => {
              if (refreshed && error.config) {
                resolve(instance.request(error.config));
              } else {
                reject(error);
              }
            });
          });
        }
      } else {
        // This code should never run, but on the offchance we receive a 401 from an unknown route then we should cleanup all sessions
        store.dispatch(clearSession());
        store.dispatch(clearOpenReportingSession());
      }
    } else {
      return Promise.reject(error);
    }
  },
);
instance.defaults.timeout = 30000;

export default instance;
